/**
 * Our own editor, for the component types Verawood no longer ships one for.
 *
 * Two types land here, in a course and in a library alike: `html` (text) and `problem`. Both
 * store their whole content in the block's `data` field — HTML in one case, OLX in the other
 * — so one editor serves both, differing only in how the content is presented and previewed.
 *
 * WHY THIS IS A SOURCE EDITOR and not a rich-text one, for now: the replacement the
 * platform points to is a React editor inside frontend-app-authoring, which this project
 * exists in order not to run. A source editor with a live preview is what Studio's own
 * "Advanced editor" always was, it is honest about what it is doing, and it cannot silently
 * mangle an author's markup the way a half-built WYSIWYG does. A rich-text mode for `html`
 * is the obvious next step, and it can be added here without touching anything else.
 *
 * The NAME is a field here rather than something to hand-edit inside the source. In a course
 * it is a settings field the source never showed; in a library it was an attribute in the raw
 * OLX, which is the same thing said badly. Both write it as metadata.
 *
 * Saving writes a DRAFT, like every other block write — the unit or the library still has to
 * be published before a learner sees it. The footer says which, because an author who has
 * pressed Save is entitled to know whether they are done.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Badge, Banner, Button, Icon } from '../../shared/ui';
import { saveBlockContent, type EditorKind } from '../../shared/lib/editor';
import './data-editor.css';

export function DataEditor({
  blockKey,
  kind,
  blockType,
  initialData,
  initialName,
  publishTarget,
  readOnly = false,
  onSaved,
}: {
  blockKey: string;
  kind: EditorKind;
  blockType: string;
  initialData: string;
  initialName: string;
  publishTarget: 'unit' | 'library';
  readOnly?: boolean;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(initialData);
  const [name, setName] = useState(initialName);
  const [preview, setPreview] = useState(kind === 'html');

  /**
   * What is currently on the server, as far as this editor knows.
   *
   * Tracked here rather than compared against the `initial*` props, and that is not a
   * refinement — it is a correctness fix. The props come from the capability probe, and the
   * platform does not always hand back byte-for-byte what it was sent (a block may normalise
   * its own `data` on save). Comparing against them left the editor reporting "Unsaved
   * changes" after a save that had demonstrably worked, and never showing the one sentence
   * that says what to do next.
   *
   * It also means a refetch cannot overwrite what an author is in the middle of typing.
   */
  const [baseline, setBaseline] = useState({ data: initialData, name: initialName });

  const trimmed = name.trim();
  const renamed = trimmed !== baseline.name && trimmed !== '';
  const dirty = value !== baseline.data || renamed;

  const save = useMutation({
    mutationFn: () =>
      saveBlockContent(blockKey, {
        data: value,
        // Only sent when it changed. A blank name is not a clear — it would replace the
        // block's own default with nothing, leaving an unnameable row in every list.
        ...(renamed ? { displayName: trimmed } : {}),
      }),
    onSuccess: () => {
      setBaseline({ data: value, name: renamed ? trimmed : baseline.name });
      onSaved();
    },
  });

  return (
    <div className="data-editor">
      <div className="data-editor__bar">
        <Badge tone="neutral">{kind === 'html' ? 'HTML' : `OLX · ${blockType}`}</Badge>
        {kind === 'html' && (
          <button
            type="button"
            className="data-editor__toggle"
            onClick={() => setPreview(!preview)}
            aria-pressed={preview}
          >
            <Icon name={preview ? 'visibility_off' : 'visibility'} />
            {preview ? 'Hide preview' : 'Show preview'}
          </button>
        )}
        <span className="data-editor__spacer" />
        {dirty && <span className="data-editor__dirty">Unsaved changes</span>}
      </div>

      {!readOnly && (
        <label className="data-editor__name">
          <span className="data-editor__label">Name</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={baseline.name}
          />
        </label>
      )}

      <div className="data-editor__panes" data-split={preview || undefined}>
        <label className="data-editor__source">
          <span className="data-editor__label">
            {kind === 'html' ? 'HTML source' : 'Source'}
          </span>
          <textarea
            value={value}
            readOnly={readOnly}
            onChange={(event) => setValue(event.target.value)}
            spellCheck={kind === 'html'}
            // Tab should indent source, not leave the field — but only in the OLX editor,
            // where structure matters and there is nowhere else for Tab to usefully go.
            onKeyDown={(event) => {
              if (readOnly || kind !== 'olx' || event.key !== 'Tab') return;
              event.preventDefault();
              const el = event.currentTarget;
              const { selectionStart: start, selectionEnd: end } = el;
              setValue(`${value.slice(0, start)}  ${value.slice(end)}`);
              requestAnimationFrame(() => el.setSelectionRange(start + 2, start + 2));
            }}
          />
        </label>

        {preview && kind === 'html' && (
          <div className="data-editor__preview">
            <span className="data-editor__label">Preview</span>
            {/*
              The author is editing this markup and is already trusted to author content for
              their own course, so rendering it is the point rather than a risk being taken
              accidentally. It is scoped to the preview pane and never rendered for a
              learner from here — learners get the platform's own rendering, with the
              platform's own sanitising.
            */}
            <div
              className="data-editor__rendered"
              dangerouslySetInnerHTML={{ __html: value }}
            />
          </div>
        )}
      </div>

      {save.isError && (
        <Banner tone="danger">
          <strong>Not saved.</strong> The platform rejected the content, so the component is
          unchanged.
          {kind === 'olx' ? (
            <>
              {' '}
              OLX is parsed as XML, which is stricter than HTML: the usual causes are an unclosed
              tag, an unescaped <code>&amp;</code> or <code>&lt;</code>, and HTML entities such as{' '}
              <code>&amp;nbsp;</code> or <code>&amp;mdash;</code> — XML only defines{' '}
              <code>&amp;amp;</code>, <code>&amp;lt;</code>, <code>&amp;gt;</code>,{' '}
              <code>&amp;quot;</code> and <code>&amp;apos;</code>, so use a real character or a
              numeric reference like <code>&amp;#8212;</code> instead.
            </>
          ) : (
            ' Check the markup and try again.'
          )}
        </Banner>
      )}

      {save.isSuccess && !dirty && (
        <Banner tone="success">
          <strong>Saved as a draft.</strong>{' '}
          {publishTarget === 'library'
            ? 'Publish the library to put it in front of learners.'
            : 'Publish the unit to show it to learners.'}
        </Banner>
      )}

      {!readOnly && (
        <div className="data-editor__actions">
          <Button
            variant="primary"
            busy={save.isPending}
            disabled={!dirty}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
          {dirty && (
            <Button
              variant="ghost"
              onClick={() => {
                setValue(baseline.data);
                setName(baseline.name);
              }}
            >
              Revert
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
